/**
 * MED-1389: bookings made through a group booking link that did not attach
 * to a session. Each one holds the coach's time and sent an invite, but no
 * session shows it, so staff see it here and can cancel it.
 *
 * MED-1399: staff and the coach of the booking link can link a booking to a
 * cohort participant ("Link to participant"). The coach cohort page shows
 * the section with only the coach's own bookings (the API scopes them).
 *
 * MED-1399 QA round 2: a booking left as "Session already booked", whose
 * member's current booking is still to come, gets "Replace current booking"
 * (staff and the booking's coach). A confirm names both bookings.
 *
 * MED-1399 QA round 3: the coach page gets Cancel booking too, every action
 * shows under impersonation, and a "Session already booked" row offers
 * Replace instead of Link to participant.
 *
 * MED-1417: staff add a 1:1 session for a member with no open 1:1 and link
 * the booking to it ("Add session and link"). Coaches never see it.
 *
 * Shown in the Sessions tab under 1:1 Coaching, in the same section and row
 * style. The coach page shows it during impersonation too (QA round 2).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Select,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Calendar } from 'react-feather';
import {
  cohortPublicBookingsApi,
  type LinkableMember,
  type UnattachedBookingOutcome,
  type UnattachedPublicBooking,
} from '../../../../services/api/cohortPublicBookings';
import { formatDateTime } from '../../../../utils/formatters';
import { ParticipantAvatar } from '../../../../components/common/ParticipantAvatar';
import { GroupPill } from './OneOnOneCoachingSection';
import styles from './UnattachedBookingsSection.module.css';

const OUTCOME_LABELS: Record<UnattachedBookingOutcome, string> = {
  unknown_email: 'Email not recognized',
  not_in_group: 'Not in this group',
  already_scheduled: 'Session already booked',
  no_unscheduled_session: 'No open session',
};

const OUTCOME_HINTS: Record<UnattachedBookingOutcome, string> = {
  unknown_email: 'No participant uses this email address.',
  not_in_group: 'This person is not an active participant in the group.',
  already_scheduled: 'The session for this booking was already booked.',
  no_unscheduled_session: 'This participant has no open session to fill.',
};

function nameParts(name: string): { first: string; last: string } {
  const [first = '', ...rest] = name.trim().split(/\s+/);
  return { first, last: rest.join(' ') };
}

/** The link refusals that mean the booking itself changed or is gone. */
const BOOKING_CHANGED_CODES = new Set([
  'already_linked',
  'booking_not_linkable',
  'row_taken',
]);

function memberName(member: LinkableMember): string {
  return [member.first_name, member.last_name].filter(Boolean).join(' ');
}

interface UnattachedBookingsSectionProps {
  cohortId: string;
  /** Render only when there is at least one booking (the All tab). */
  hideWhenEmpty?: boolean;
  /**
   * MED-1399: the coach page. Only the bookings on the caller's own links.
   */
  coachView?: boolean;
  /** MED-1399: called after a link, so the page shows the filled session. */
  onLinked?: () => void;
}

export function UnattachedBookingsSection({
  cohortId,
  hideWhenEmpty = false,
  coachView = false,
  onLinked,
}: UnattachedBookingsSectionProps) {
  const [bookings, setBookings] = useState<UnattachedPublicBooking[] | null>(
    null
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const busy =
    cancelingId !== null || replacingId !== null || addingId !== null;
  // A fast double click must not open a second confirm.
  const confirmOpen = useRef(false);
  // The modal keeps its booking while it fades out; linkOpen drives it.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState<UnattachedPublicBooking | null>(
    null
  );
  const [members, setMembers] = useState<LinkableMember[] | null>(null);
  const [membersFailed, setMembersFailed] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  // Only the answer for the booking that is open now may fill the picker.
  const membersRequest = useRef(0);

  // The booking changed or is gone: close, say why, and show the list now.
  const closeIfChanged = (error: unknown, message: string): boolean => {
    const { code, status } = error as { code?: string; status?: number };
    if (status !== 404 && !(code && BOOKING_CHANGED_CODES.has(code))) {
      return false;
    }
    setLinkOpen(false);
    notifications.show({ title: 'Booking not linked', message, color: 'red' });
    void load();
    return true;
  };

  const loadMembers = (booking: UnattachedPublicBooking) => {
    const request = ++membersRequest.current;
    setMembers(null);
    setMembersFailed(false);
    cohortPublicBookingsApi
      .linkableMembers(cohortId, booking.id)
      .then(found => {
        if (membersRequest.current === request) setMembers(found);
      })
      .catch(error => {
        if (membersRequest.current !== request) return;
        if (
          !closeIfChanged(
            error,
            'This booking changed. It is no longer open to link.'
          )
        ) {
          setMembersFailed(true);
        }
      });
  };

  const openLink = (booking: UnattachedPublicBooking) => {
    if (busy || confirmOpen.current) return;
    setLinkTarget(booking);
    setMemberId(null);
    setLinkError(null);
    setLinkOpen(true);
    loadMembers(booking);
  };

  const closeLink = () => {
    if (linking) return;
    membersRequest.current += 1;
    setLinkOpen(false);
  };

  const linkBooking = async () => {
    if (!linkTarget || !memberId || linking) return;
    setLinking(true);
    setLinkError(null);
    try {
      await cohortPublicBookingsApi.link(cohortId, linkTarget.id, memberId);
      const member = members?.find(m => m.user_id === memberId);
      setBookings(current =>
        current ? current.filter(b => b.id !== linkTarget.id) : current
      );
      setLinkOpen(false);
      notifications.show({
        title: 'Booking linked',
        message: `It is now on the session of ${member ? memberName(member) : 'the participant'}. Nobody got an email.`,
        color: 'green',
      });
      onLinked?.();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'We could not link this booking. Try again in a few minutes.';
      if (closeIfChanged(error, message)) return;
      // Not a participant, or no open session: keep the picker open so the
      // user can choose another participant.
      setLinkError(message);
    } finally {
      setLinking(false);
    }
  };

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      setBookings(
        await cohortPublicBookingsApi.listUnattached(cohortId, coachView)
      );
    } catch {
      setLoadFailed(true);
    }
  }, [cohortId, coachView]);

  useEffect(() => {
    setBookings(null);
    void load();
  }, [load]);

  const cancelBooking = async (booking: UnattachedPublicBooking) => {
    setCancelingId(booking.id);
    try {
      await cohortPublicBookingsApi.cancel(cohortId, booking.id);
      setBookings(current =>
        current ? current.filter(b => b.id !== booking.id) : current
      );
      notifications.show({
        title: 'Booking canceled',
        message: `${booking.booker_name} and the coach get a cancellation email.`,
        color: 'green',
      });
    } catch (error) {
      // The booking is on a Calendly account the coach no longer has
      // connected, or the coach has no Calendly connection at all. A retry
      // never works: offer to take it off the list. The server says which.
      if ((error as { code?: string }).code === 'calendly_booking_not_reachable') {
        confirmRemove(booking, (error as Error).message);
        return;
      }
      // A 409 means the booking changed (attached or canceled): show it now.
      void load();
      notifications.show({
        title: 'Booking not canceled',
        message:
          error instanceof Error
            ? error.message
            : 'We could not cancel this booking. Try again in a few minutes.',
        color: 'red',
      });
    } finally {
      setCancelingId(null);
    }
  };

  const removeBooking = async (booking: UnattachedPublicBooking) => {
    setCancelingId(booking.id);
    try {
      await cohortPublicBookingsApi.remove(cohortId, booking.id);
      setBookings(current =>
        current ? current.filter(b => b.id !== booking.id) : current
      );
      notifications.show({
        title: 'Booking removed',
        message: 'It is off this list. Nobody got an email.',
        color: 'green',
      });
    } catch (error) {
      void load();
      notifications.show({
        title: 'Booking not removed',
        message:
          error instanceof Error
            ? error.message
            : 'We could not remove this booking. Try again in a few minutes.',
        color: 'red',
      });
    } finally {
      setCancelingId(null);
    }
  };

  const confirmRemove = (booking: UnattachedPublicBooking, reason: string) => {
    if (confirmOpen.current) return;
    confirmOpen.current = true;
    modals.openConfirmModal({
      title: 'Medley cannot cancel this in Calendly',
      centered: true,
      children: (
        <Box>
          <Text size="sm">{reason} Remove it from this list?</Text>
          <Text size="sm" c="dimmed" mt="sm">
            Nobody gets an email. If the event is still in a Calendly account,
            the coach must cancel it there.
          </Text>
        </Box>
      ),
      labels: { confirm: 'Remove from list', cancel: 'Keep on list' },
      confirmProps: { color: 'red' },
      onConfirm: () => void removeBooking(booking),
      onClose: () => {
        confirmOpen.current = false;
      },
    });
  };

  const addSession = async (booking: UnattachedPublicBooking) => {
    if (!booking.matched_user_id) return;
    setAddingId(booking.id);
    try {
      await cohortPublicBookingsApi.addSession(
        cohortId,
        booking.id,
        booking.matched_user_id
      );
      setBookings(current =>
        current ? current.filter(b => b.id !== booking.id) : current
      );
      notifications.show({
        title: 'Session added',
        message: `The booking is now on a new session of ${booking.matched_user_name ?? booking.booker_name}. Nobody got an email.`,
        color: 'green',
      });
      onLinked?.();
    } catch (error) {
      // The booking or the member's sessions changed: show the list now.
      void load();
      notifications.show({
        title: 'Session not added',
        message:
          error instanceof Error
            ? error.message
            : 'We could not add this session. Try again in a few minutes.',
        color: 'red',
      });
    } finally {
      setAddingId(null);
    }
  };

  const confirmAddSession = (booking: UnattachedPublicBooking) => {
    if (busy || confirmOpen.current) return;
    confirmOpen.current = true;
    modals.openConfirmModal({
      title: 'Add session and link',
      centered: true,
      children: (
        <Box>
          <Text size="sm">
            Add a new {booking.touchpoint_label} for{' '}
            <strong>{booking.matched_user_name ?? booking.booker_name}</strong>{' '}
            and link the booking for {formatDateTime(booking.scheduled_start)}{' '}
            to it?
          </Text>
          <Text size="sm" c="dimmed" mt="sm">
            This adds a session for this member. It counts as a delivered 1:1 if
            it happens. Nobody gets an email, and the calendar invite does not
            change.
          </Text>
        </Box>
      ),
      labels: { confirm: 'Add session', cancel: 'Keep as is' },
      onConfirm: () => void addSession(booking),
      onClose: () => {
        confirmOpen.current = false;
      },
    });
  };

  const replaceBooking = async (booking: UnattachedPublicBooking) => {
    if (!booking.replace) return;
    setReplacingId(booking.id);
    try {
      await cohortPublicBookingsApi.replace(
        cohortId,
        booking.id,
        booking.replace
      );
      setBookings(current =>
        current ? current.filter(b => b.id !== booking.id) : current
      );
      notifications.show({
        title: 'Booking replaced',
        message: `The session now holds this booking. The earlier booking was canceled, and its guests got a cancellation notice.`,
        color: 'green',
      });
      onLinked?.();
    } catch (error) {
      // replace_not_linked: the earlier booking is canceled and the session
      // is open, so the sessions show it. Otherwise nothing changed.
      const canceledOnly =
        (error as { code?: string }).code === 'replace_not_linked';
      if (canceledOnly) onLinked?.();
      void load();
      notifications.show({
        title: canceledOnly
          ? 'Earlier booking canceled'
          : 'Booking not replaced',
        message:
          error instanceof Error
            ? error.message
            : 'We could not replace this booking. Try again in a few minutes.',
        color: 'red',
      });
    } finally {
      setReplacingId(null);
    }
  };

  const confirmReplace = (booking: UnattachedPublicBooking) => {
    const current = booking.replace;
    if (!current || busy || confirmOpen.current) return;
    confirmOpen.current = true;
    const kind =
      booking.touchpoint_label.charAt(0).toLowerCase() +
      booking.touchpoint_label.slice(1);
    modals.openConfirmModal({
      title: 'Replace current booking',
      centered: true,
      children: (
        <Box>
          <Text size="sm">
            <strong>{current.member_name ?? booking.booker_name}</strong> has{' '}
            {/^[aeiou]/i.test(kind) ? 'an' : 'a'} {kind} booked for{' '}
            <strong>{formatDateTime(current.scheduled_start)}</strong>
            {current.coach_name ? ` with ${current.coach_name}` : ''}. Replace
            it with the booking for{' '}
            <strong>{formatDateTime(booking.scheduled_start)}</strong>
            {booking.coach_name ? ` with ${booking.coach_name}` : ''}?
          </Text>
          <Text size="sm" c="dimmed" mt="sm">
            The current booking is canceled, and its guests get a cancellation
            notice. The session moves to the new booking. This cannot be undone.
          </Text>
        </Box>
      ),
      labels: { confirm: 'Replace booking', cancel: 'Keep current booking' },
      confirmProps: { color: 'red' },
      onConfirm: () => void replaceBooking(booking),
      onClose: () => {
        confirmOpen.current = false;
      },
    });
  };

  const confirmCancel = (booking: UnattachedPublicBooking) => {
    if (busy || confirmOpen.current) return;
    confirmOpen.current = true;
    const vendor =
      booking.booked_with === 'google_calendar'
        ? 'The Google Calendar invite is deleted'
        : 'Calendly cancels the event';
    modals.openConfirmModal({
      title: 'Cancel booking',
      centered: true,
      children: (
        <Box>
          <Text size="sm">
            Cancel the {booking.touchpoint_label} that{' '}
            <strong>{booking.booker_name}</strong> booked for{' '}
            {formatDateTime(booking.scheduled_start)}?
          </Text>
          <Text size="sm" c="dimmed" mt="sm">
            {vendor}, and the booker and the coach get a cancellation email. The
            coach&apos;s time opens again. This cannot be undone.
          </Text>
        </Box>
      ),
      labels: { confirm: 'Cancel booking', cancel: 'Keep booking' },
      confirmProps: { color: 'red' },
      onConfirm: () => void cancelBooking(booking),
      onClose: () => {
        confirmOpen.current = false;
      },
    });
  };

  if (hideWhenEmpty && (!bookings || bookings.length === 0)) return null;

  const count = bookings?.length ?? 0;
  const subtitle =
    count === 0
      ? 'Booked through a booking link, not linked to a session'
      : `${count} upcoming booking${count !== 1 ? 's' : ''} not linked to a session`;

  return (
    <Box className={styles.section}>
      <Group gap="sm" wrap="nowrap" className={styles.header}>
        <ThemeIcon color="gray" size={40} radius="md" variant="filled">
          <Calendar size={20} />
        </ThemeIcon>
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="16px">
            Unlinked bookings
          </Text>
          <Text size="13px" c="dimmed" mt={4}>
            {subtitle}
          </Text>
        </Box>
      </Group>

      <Box className={styles.list}>
        {loadFailed ? (
          <Group justify="center" gap="sm" p="xl">
            <Text size="sm" c="dimmed">
              We could not load these bookings.
            </Text>
            <Button variant="default" size="xs" radius="sm" onClick={load}>
              Try again
            </Button>
          </Group>
        ) : bookings === null ? (
          <Center p="xl">
            <Loader size="sm" />
          </Center>
        ) : bookings.length === 0 ? (
          <Text size="sm" c="dimmed" p="xl" ta="center">
            No unlinked bookings. Every upcoming booking made through a booking
            link is on a participant&apos;s session.
          </Text>
        ) : (
          bookings.map(booking => {
            const { first, last } = nameParts(booking.booker_name);
            return (
              <Box key={booking.id} className={styles.row}>
                <Box className={styles.who}>
                  <ParticipantAvatar
                    firstName={first}
                    lastName={last}
                    size={40}
                  />
                  <Box className={styles.whoText}>
                    <Box className={styles.nameLine}>
                      <Text
                        fw={600}
                        size="sm"
                        className={styles.ellipsis}
                        title={booking.booker_name}
                      >
                        {booking.booker_name}
                      </Text>
                    </Box>
                    <Text
                      size="12px"
                      c="dimmed"
                      className={styles.ellipsis}
                      title={booking.booker_email}
                    >
                      {booking.booker_email}
                    </Text>
                  </Box>
                </Box>

                <Box className={styles.when}>
                  <Text size="sm" fw={500}>
                    {formatDateTime(booking.scheduled_start)}
                  </Text>
                  <Text size="12px" c="dimmed">
                    {booking.touchpoint_label}
                    {booking.coach_name ? ` with ${booking.coach_name}` : ''}
                    {booking.booked_with === 'google_calendar'
                      ? ' · Google Calendar invite'
                      : ' · Calendly'}
                  </Text>
                  {/* The group whose link was used, not the booker's group. */}
                  <Box mt={4} style={{ display: 'flex', minWidth: 0 }}>
                    <GroupPill
                      groupName={booking.group_name}
                      groupId={booking.group_id}
                      label={`Via ${booking.group_name} link`}
                      title={`Booked through the booking link of ${booking.group_name}`}
                    />
                  </Box>
                </Box>

                <Box
                  className={styles.reason}
                  title={OUTCOME_HINTS[booking.attach_outcome]}
                >
                  <Box className={styles.dot} aria-hidden />
                  <Box style={{ minWidth: 0 }}>
                    <Text size="13px">
                      {OUTCOME_LABELS[booking.attach_outcome]}
                    </Text>
                    {booking.status === 'unconfirmed' && (
                      <Text size="12px" c="dimmed">
                        Calendly did not confirm it
                      </Text>
                    )}
                  </Box>
                </Box>

                <Group
                  gap="xs"
                  wrap="wrap"
                  justify="flex-end"
                  className={styles.action}
                >
                  {/* A duplicate offers Replace, not a link (QA round 3). */}
                  {booking.attach_outcome !== 'already_scheduled' && (
                    <Button
                      variant="default"
                      size="xs"
                      radius="sm"
                      // An unconfirmed row says "Calendly did not confirm it".
                      disabled={busy || booking.status !== 'scheduled'}
                      onClick={() => openLink(booking)}
                    >
                      Link to participant
                    </Button>
                  )}
                  {/* Staff only: the new session counts toward coach pay. */}
                  {!coachView &&
                    booking.attach_outcome === 'no_unscheduled_session' &&
                    booking.touchpoint === 'one_on_one' &&
                    booking.matched_user_id && (
                      <Button
                        variant="default"
                        size="xs"
                        radius="sm"
                        loading={addingId === booking.id}
                        disabled={
                          (busy && addingId !== booking.id) ||
                          booking.status !== 'scheduled'
                        }
                        onClick={() => confirmAddSession(booking)}
                      >
                        Add session and link
                      </Button>
                    )}
                  {booking.replace && (
                    <Button
                      variant="default"
                      size="xs"
                      radius="sm"
                      loading={replacingId === booking.id}
                      disabled={busy && replacingId !== booking.id}
                      onClick={() => confirmReplace(booking)}
                    >
                      Replace current booking
                    </Button>
                  )}
                  <Button
                    variant="default"
                    size="xs"
                    radius="sm"
                    color="red"
                    loading={cancelingId === booking.id}
                    disabled={busy && cancelingId !== booking.id}
                    onClick={() => confirmCancel(booking)}
                  >
                    Cancel booking
                  </Button>
                </Group>
              </Box>
            );
          })
        )}
      </Box>

      <Modal
        opened={linkOpen}
        onClose={closeLink}
        title="Link to participant"
        centered
      >
        {linkTarget && (
          <Box>
            <Text size="sm">
              Link the {linkTarget.touchpoint_label} that{' '}
              <strong>{linkTarget.booker_name}</strong> booked for{' '}
              {formatDateTime(linkTarget.scheduled_start)} to a participant of
              this cohort.
            </Text>
            <Text size="sm" c="dimmed" mt="sm">
              The booking fills the participant&apos;s next open session of this
              kind. Nobody gets an email, and the calendar invite does not
              change.
            </Text>
            {membersFailed ? (
              <Group gap="sm" mt="md">
                <Text size="sm" c="dimmed">
                  We could not load the participants.
                </Text>
                <Button
                  variant="default"
                  size="xs"
                  radius="sm"
                  onClick={() => loadMembers(linkTarget)}
                >
                  Try again
                </Button>
              </Group>
            ) : (
              <Select
                mt="md"
                label="Participant"
                description="Active participants in the groups of this coach"
                placeholder={
                  members === null
                    ? 'Loading participants'
                    : members.length === 0
                      ? 'No participant can take this booking'
                      : 'Search by name or email'
                }
                searchable
                nothingFoundMessage="No participant matches"
                disabled={members === null || members.length === 0 || linking}
                data={(members ?? []).map(m => ({
                  value: m.user_id,
                  label: `${memberName(m)} (${m.email}), ${m.group_name}`,
                }))}
                value={memberId}
                onChange={value => {
                  setMemberId(value);
                  setLinkError(null);
                }}
                comboboxProps={{ withinPortal: true }}
              />
            )}
            {linkError && (
              <Text size="sm" c="red" mt="sm" role="alert">
                {linkError}
              </Text>
            )}
            <Group justify="flex-end" gap="sm" mt="lg">
              <Button
                variant="default"
                radius="sm"
                onClick={closeLink}
                disabled={linking}
              >
                Close
              </Button>
              <Button
                radius="sm"
                onClick={() => void linkBooking()}
                disabled={!memberId}
                loading={linking}
              >
                Link booking
              </Button>
            </Group>
          </Box>
        )}
      </Modal>
    </Box>
  );
}

export default UnattachedBookingsSection;
